import { useFormik } from 'formik';
import RootLayout from '../../layouts/RootLayout';
import FormInput from '../../../components/forms/FormInput';
import { Button } from '../../../components/buttons/Button';
import { useAuth } from '../../../lib/auth';
import API from '../../../lib/api.ts';
import { transformLaravelToFormikErrors } from '../../../lib/helpers.ts';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import Alert from '../../../components/Alert.tsx';
import { SchemaDrivenFormFieldProps } from '../../../lib/types.ts';
import HeadLogo from '../../../components/logos/HeadLogo.tsx';

export default function ResetPasswordPage() {
    useAuth({
        middleware: 'guest',
        redirectTo: '/dashboard',
        tmp: 'ResetPasswordPage',
    });

    const navigate = useNavigate();

    const [passwordResetStatus, setPasswordResetStatus] = useState<
        undefined | string
    >(undefined);

    const { token } = useParams();
    const [searchParams] = useSearchParams();

    const {
        handleSubmit,
        handleChange,
        values,
        setSubmitting,
        isSubmitting,
        errors,
        touched,
        setErrors,
    } = useFormik({
        initialValues: {
            email: searchParams.get('email') ?? '',
            password: '',
            password_confirmation: '',
        },
        onSubmit: (values) => {
            API.post('/reset-password', { ...values, token })
                .then((res) => {
                    setPasswordResetStatus(
                        res?.data?.status ?? 'Heslo bylo úspěšně obnoveno.'
                    );
                    setTimeout(() => {
                        navigate('/');
                    }, 5000);
                })
                .catch((err) => {
                    const errors = err?.response?.data?.errors;
                    if (errors)
                        setErrors(transformLaravelToFormikErrors(errors));
                })
                .finally(() => setSubmitting(false));
        },
    });

    const schema: SchemaDrivenFormFieldProps[] = [
        {
            name: 'email',
            type: 'email',
            label: 'E-mail',
        },
        {
            name: 'password',
            type: 'password',
            label: 'Nové heslo',
        },
        {
            name: 'password_confirmation',
            type: 'password',
            label: 'Nové heslo znovu',
        },
    ];

    return (
        <RootLayout>
            <main className="container mx-auto flex h-screen flex-col items-center justify-start py-32">
                <HeadLogo />
                <h1>Obnova hesla</h1>
                <p className="mb-6 text-ink-800">
                    Vytvořte si nové heslo, které budete používat pro vstup do
                    aplikace.
                </p>
                {passwordResetStatus !== undefined ? (
                    <Alert type="success">{passwordResetStatus}</Alert>
                ) : (
                    <div className="w-full max-w-[520px]">
                        <form
                            onSubmit={handleSubmit}
                            className="mx-auto flex w-full max-w-[400px] flex-col space-y-4"
                        >
                            {schema.map(
                                ({ type, name, label, placeholder }) => (
                                    <FormInput
                                        key={name}
                                        name={name}
                                        label={label!}
                                        onChange={handleChange}
                                        value={
                                            values[name as keyof typeof values]
                                        }
                                        type={type}
                                        error={
                                            errors[
                                                name as keyof typeof errors
                                            ] &&
                                            touched[
                                                name as keyof typeof touched
                                            ]
                                                ? errors[
                                                      name as keyof typeof errors
                                                  ]
                                                : null
                                        }
                                        placeholder={placeholder}
                                    />
                                )
                            )}

                            <Button
                                type="submit"
                                color="product"
                                isLoading={isSubmitting}
                                className="text-base"
                            >
                                Obnovit heslo
                            </Button>
                        </form>
                    </div>
                )}
            </main>
        </RootLayout>
    );
}
